import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount, useSignMessage } from 'wagmi';
import { userAPI } from '../services/api';
import './ProfileSetup.css';

export function ProfileSetup() {
  const { address, isConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    bio: '',
    profileImage: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingProfile, setCheckingProfile] = useState(true);

  useEffect(() => {
    if (!isConnected || !address) {
      navigate('/');
      return;
    }

    checkExistingProfile();
  }, [address, isConnected]);

  const checkExistingProfile = async () => {
    if (!address) return;

    try {
      const response = await userAPI.checkProfile(address);
      if (response.exists) {
        // Profile already exists, redirect to profile page
        navigate('/profile');
      }
    } catch (err) {
      console.error('Error checking profile:', err);
    } finally {
      setCheckingProfile(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!address) {
      setError('Wallet not connected');
      return;
    }

    if (!formData.name || !formData.email) {
      setError('Name and email are required');
      return;
    }

    // Validate email
    const emailRegex = /^\S+@\S+\.\S+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      // Sign message to prove ownership
      const message = `I am creating my profile on Book of Secrets\nWallet: ${address}\nTimestamp: ${Date.now()}`;
      const signature = await signMessageAsync({ message });

      // Send to backend
      await userAPI.createOrUpdateProfile({
        walletAddress: address,
        name: formData.name,
        email: formData.email,
        bio: formData.bio,
        profileImage: formData.profileImage,
        signature,
        message
      });

      // Success - redirect to profile
      navigate('/profile');
    } catch (err: any) {
      console.error('Profile creation error:', err);
      setError(err.response?.data?.message || 'Failed to create profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checkingProfile) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p className="loading-text">Checking profile...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="profile-setup-container">
        <div className="setup-header">
          <h1 className="setup-title">Complete Your Profile</h1>
          <p className="setup-subtitle">
            Set up your profile to start creating secret pages
          </p>
        </div>

        <form className="setup-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="name">
              Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-input"
              placeholder="Enter your name"
              value={formData.name}
              onChange={handleInputChange}
              maxLength={50}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="email">
              Email <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="form-input"
              placeholder="your@email.com"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="bio">
              Bio <span className="optional">(optional)</span>
            </label>
            <textarea
              id="bio"
              name="bio"
              className="form-textarea"
              placeholder="Tell us about yourself..."
              value={formData.bio}
              onChange={handleInputChange}
              maxLength={500}
              rows={4}
            />
            <div className="char-count">{formData.bio.length} / 500</div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="profileImage">
              Profile Image URL <span className="optional">(optional)</span>
            </label>
            <input
              type="url"
              id="profileImage"
              name="profileImage"
              className="form-input"
              placeholder="https://example.com/image.jpg"
              value={formData.profileImage}
              onChange={handleInputChange}
            />
            <p className="form-help">
              Paste a direct link to your profile image
            </p>
          </div>

          {formData.profileImage && (
            <div className="image-preview">
              <p className="preview-label">Preview:</p>
              <img
                src={formData.profileImage}
                alt="Profile preview"
                className="preview-image"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/')}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="btn-spinner"></span>
                  Creating Profile...
                </>
              ) : (
                <>
                  <span>✨</span>
                  Create Profile
                </>
              )}
            </button>
          </div>

          <div className="signature-note">
            <p>
              🔒 You'll be asked to sign a message with your wallet to verify ownership.
              This is free and doesn't cost any gas.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
